import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Database, Star, TrendingUp, TrendingDown, Microscope, LogIn, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = ["1m", "3m", "6m", "1y", "3y", "5y"];
const fmtR = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`);

export function Stars({ n }) {
  if (!n) return <span className="text-xs text-[var(--ink-muted)]">NR</span>;
  return (
    <span className="inline-flex items-center gap-0.5" title={`${n} star`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </span>
  );
}

function ComputingBanner({ status, onRefresh }) {
  return (
    <Card className="glass-card border-0"><CardContent className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
      <RefreshCw className="h-6 w-6 shrink-0 animate-spin text-[var(--cta)]" />
      <div className="min-w-0">
        <div className="font-medium text-[var(--brand)]" data-testid="research-computing">Research engine is crunching NAV history…</div>
        <div className="text-sm text-[var(--ink-muted)]">{status?.done || 0}{status?.total ? ` / ${status.total}` : ""} funds analysed — ratings, movers and model portfolios appear as soon as the first pass completes (few minutes).</div>
      </div>
      <Button variant="outline" className="w-full rounded-full sm:ml-auto sm:w-auto" onClick={onRefresh} data-testid="research-refresh-button">Refresh</Button>
    </CardContent></Card>
  );
}

function Movers({ movers }) {
  const [period, setPeriod] = useState("1y");
  const m = movers?.[period];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button key={p} className={`rounded-full px-4 py-1.5 text-xs font-medium ${period === p ? "bg-[var(--brand)] text-white" : "bg-[var(--surface-2)] text-[var(--ink-muted)]"}`} onClick={() => setPeriod(p)} data-testid={`movers-period-${p}`}>{p.toUpperCase()}</button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {[["Top performers", m?.top, TrendingUp, "text-emerald-600"], ["Laggards", m?.bottom, TrendingDown, "text-red-500"]].map(([title, rows, Icon, tone]) => (
          <Card key={title} className="border-[var(--glass-border)]"><CardContent className="p-0">
            <div className={`flex items-center gap-2 border-b border-[var(--glass-border)] p-3 text-sm font-medium ${tone}`}><Icon className="h-4 w-4" /> {title} · {period.toUpperCase()}</div>
            <div className="divide-y divide-[var(--glass-border)]" data-testid={`movers-${title === "Top performers" ? "top" : "bottom"}`}>
              {(rows || []).map((r) => (
                <div key={r.scheme_code} className="flex items-center justify-between px-3 py-2 text-xs">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-[var(--brand)]">{r.scheme_name}</div>
                    <div className="text-[var(--ink-muted)]">{r.category}</div>
                  </div>
                  <div className="ml-3 text-right">
                    <div className={`font-semibold ${r.ret >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtR(r.ret)}</div>
                    <Stars n={r.stars} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

function Screener() {
  const [data, setData] = useState({ funds: [], categories: [] });
  const [cat, setCat] = useState("");
  const [minStars, setMinStars] = useState(0);
  const [sort, setSort] = useState("composite");
  useEffect(() => {
    axios.get(`${API}/research/screener?limit=60${cat ? `&category=${encodeURIComponent(cat)}` : ""}${minStars ? `&min_stars=${minStars}` : ""}&sort=${sort}&period=3y`).then((r) => setData(r.data));
  }, [cat, minStars, sort]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="screener-category-select">
          <option value="">All categories</option>
          {data.categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={minStars} onChange={(e) => setMinStars(+e.target.value)} data-testid="screener-stars-select">
          <option value={0}>Any rating</option>
          <option value={4}>4★ &amp; above</option>
          <option value={5}>5★ only</option>
        </select>
        <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="screener-sort-select">
          <option value="composite">Best overall (SMIFS score)</option>
          <option value="returns">Highest 3Y return</option>
        </select>
      </div>
      <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-xs" data-testid="screener-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Fund</th><th className="p-3 font-medium">Rating</th><th className="p-3 text-right font-medium">1Y</th><th className="p-3 text-right font-medium">3Y</th><th className="p-3 text-right font-medium">5Y</th><th className="p-3 text-right font-medium">Sharpe</th><th className="p-3 text-right font-medium">Max DD</th><th className="p-3 text-right font-medium">Consistency</th><th className="p-3 text-right font-medium">Cat. rank</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {data.funds.map((f) => (
              <tr key={f.scheme_code} data-testid={`screener-row-${f.scheme_code}`}>
                <td className="max-w-[300px] p-3"><div className="truncate font-medium text-[var(--brand)]">{f.scheme_name}</div><div className="text-[var(--ink-muted)]">{f.category}</div></td>
                <td className="p-3"><Stars n={f.stars} /></td>
                <td className="p-3 text-right">{fmtR(f.returns?.["1y"])}</td>
                <td className="p-3 text-right font-medium">{fmtR(f.returns?.["3y"])}</td>
                <td className="p-3 text-right">{fmtR(f.returns?.["5y"])}</td>
                <td className="p-3 text-right">{f.sharpe ?? "—"}</td>
                <td className="p-3 text-right text-red-600">{f.max_dd_3y != null ? `${f.max_dd_3y}%` : "—"}</td>
                <td className="p-3 text-right">{f.consistency != null ? `${f.consistency}%` : "—"}</td>
                <td className="p-3 text-right">{f.category_rank}/{f.category_count}</td>
              </tr>
            ))}
            {data.funds.length === 0 && <tr><td colSpan={9} className="p-5 text-sm text-[var(--ink-muted)]">No rated funds match the filters yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

function ModelPortfolios() {
  const [ports, setPorts] = useState([]);
  useEffect(() => { axios.get(`${API}/research/model-portfolios`).then((r) => setPorts(r.data.model_portfolios || [])); }, []);
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {ports.map((p) => (
        <Card key={p.id} className="border-[var(--glass-border)]" data-testid={`model-portfolio-${p.id}`}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading text-lg text-[var(--brand)]">{p.name}</div>
                <div className="text-xs text-[var(--ink-muted)]">{p.description}</div>
              </div>
              <div className="text-right text-xs">
                <div className="rounded-full bg-[var(--surface-2)] px-2 py-0.5">{p.risk}</div>
                <div className="mt-1 text-[var(--ink-muted)]">{p.horizon}</div>
              </div>
            </div>
            <div className="my-3 rounded-xl bg-[var(--surface-2)] p-3 text-sm">
              Blended 3Y return of picks: <b className="text-[var(--cta)]">{fmtR(p.expected_return_3y)}</b> p.a.
            </div>
            <div className="divide-y divide-[var(--glass-border)]">
              {p.holdings.map((h) => (
                <div key={h.slot} className="flex items-center justify-between py-2 text-xs">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-[var(--brand)]">{h.scheme_name}</div>
                    <div className="text-[var(--ink-muted)]">{h.slot} · <Stars n={h.stars} /></div>
                  </div>
                  <div className="ml-3 text-right">
                    <div className="font-heading text-base text-[var(--brand)]">{h.weight}%</div>
                    <div className="text-[var(--ink-muted)]">3Y {fmtR(h.ret_3y)}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
      {ports.length === 0 && <div className="p-5 text-sm text-[var(--ink-muted)]">Model portfolios will appear once the engine finishes its first pass.</div>}
    </div>
  );
}

function Categories({ rows }) {
  return (
    <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
      <table className="w-full text-xs" data-testid="categories-table">
        <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
          <th className="p-3 font-medium">Category</th><th className="p-3 text-right font-medium">Funds rated</th><th className="p-3 text-right font-medium">Avg 1Y</th><th className="p-3 text-right font-medium">Avg 3Y</th><th className="p-3 font-medium">Category champion</th>
        </tr></thead>
        <tbody className="divide-y divide-[var(--glass-border)]">
          {(rows || []).map((c) => (
            <tr key={c.category}>
              <td className="p-3 font-medium text-[var(--brand)]">{c.category}</td>
              <td className="p-3 text-right">{c.count}</td>
              <td className="p-3 text-right">{fmtR(c.avg_1y)}</td>
              <td className="p-3 text-right">{fmtR(c.avg_3y)}</td>
              <td className="p-3"><span className="truncate">{c.best_fund}</span> <Stars n={c.best_stars} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

function Personalized() {
  const [data, setData] = useState(null);
  useEffect(() => { clientApi.get("/research/personalized").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  return (
    <Card className="glass-card border-0" data-testid="personalized-panel"><CardContent className="p-5">
      <div className="flex flex-wrap items-center gap-6">
        <div>
          <div className="text-xs text-[var(--ink-muted)]">My Portfolio Research</div>
          <div className="font-heading text-lg text-[var(--brand)]" data-testid="personalized-verdict">{data.summary.verdict}</div>
        </div>
        <div className="flex gap-6 text-center text-xs">
          <div><div className="text-[var(--ink-muted)]">Avg rating</div><div className="font-heading text-base text-[var(--brand)]">{data.summary.avg_stars ?? "—"}★</div></div>
          <div><div className="text-[var(--ink-muted)]">Rated holdings</div><div className="font-heading text-base text-[var(--brand)]">{data.summary.rated_holdings}{data.summary.total_holdings > data.summary.rated_holdings ? ` / ${data.summary.total_holdings}` : ""}</div></div>
          <div><div className="text-[var(--ink-muted)]">Laggards (≤2★)</div><div className={`font-heading text-base ${data.summary.laggards ? "text-red-600" : "text-emerald-700"}`}>{data.summary.laggards}</div></div>
        </div>
      </div>
      <div className="mt-4 divide-y divide-[var(--glass-border)]">
        {data.holdings.map((h) => (
          <div key={h.scheme_cd} className="py-2.5 text-xs" data-testid={`personalized-holding-${h.scheme_cd}`}>
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-[var(--brand)]">{h.scheme_name}</div>
                {h.research ? (
                  <div className="text-[var(--ink-muted)]">{h.research.category} · Rank {h.research.category_rank}/{h.research.category_count} · 3Y {fmtR(h.research.returns?.["3y"])}</div>
                ) : <div className="text-[var(--ink-muted)]">Not yet in research universe</div>}
              </div>
              <div className="ml-3">{h.research ? <Stars n={h.research.stars} /> : null}</div>
            </div>
            {h.alternatives.length > 0 && (
              <div className="mt-1.5 rounded-lg bg-amber-50 p-2 text-amber-900">
                Better-rated {h.research.category} options: {h.alternatives.map((a) => `${a.scheme_name} (${a.stars}★)`).join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

const PMS_PERIODS = ["1m", "3m", "6m", "1y", "2y", "3y", "4y", "5y", "7y", "10y", "si"];
const fmtCr = (value) => value == null ? "—" : `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;

function PmsResearch() {
  const [overview, setOverview] = useState(null);
  const [data, setData] = useState({ strategies: [], categories: [], service_types: [], providers: [], total: 0 });
  const [period, setPeriod] = useState("3y");
  const [category, setCategory] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [provider, setProvider] = useState("");
  const [minStars, setMinStars] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("return");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/research/pms/overview`)
      .then((r) => setOverview(r.data))
      .catch((err) => setError(getApiErrorMessage(err)));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ period, sort, limit: "200" });
      if (category) params.set("category", category);
      if (serviceType) params.set("service_type", serviceType);
      if (provider) params.set("provider", provider);
      if (minStars) params.set("min_stars", String(minStars));
      if (search.trim()) params.set("search", search.trim());
      setLoading(true);
      setError("");
      axios.get(`${API}/research/pms/screener?${params}`)
        .then((r) => setData(r.data))
        .catch((err) => setError(getApiErrorMessage(err)))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [period, category, serviceType, provider, minStars, search, sort]);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Investment approaches", overview?.strategies ?? "—"],
          ["PMS providers", overview?.providers ?? "—"],
          ["Rated approaches", overview?.rated_strategies ?? "—"],
          ["Reported AUM", overview ? fmtCr(overview.aum_crore) : "—"],
        ].map(([label, value]) => (
          <Card key={label} className="border-[var(--glass-border)]"><CardContent className="p-4">
            <div className="text-xs text-[var(--ink-muted)]">{label}</div>
            <div className="font-heading text-xl text-[var(--brand)]">{value}</div>
          </CardContent></Card>
        ))}
      </div>

      <Card className="border-[var(--glass-border)]"><CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--brand)]"><Database className="h-4 w-4" /> PMS investment-approach screener</div>
          <div className="text-[10px] text-[var(--ink-muted)]">
            Source: {overview?.source?.name || "APMI authorized master"}{overview?.performance_as_of ? ` · performance as of ${overview.performance_as_of}` : ""}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <input className="w-full min-w-0 flex-1 rounded-md border border-[var(--glass-border)] bg-white p-2 text-base sm:min-w-[220px] sm:text-sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search approach, provider or benchmark" data-testid="pms-research-search" />
          <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="pms-research-category">
            <option value="">All asset classes</option>
            {data.categories.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
          </select>
          <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={serviceType} onChange={(e) => setServiceType(e.target.value)} data-testid="pms-research-service-type">
            <option value="">All service types</option>
            {data.service_types.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
          </select>
          <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm sm:max-w-[240px]" value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="pms-research-provider">
            <option value="">All providers</option>
            {data.providers.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={minStars} onChange={(e) => setMinStars(Number(e.target.value))} data-testid="pms-research-stars">
            <option value={0}>Any rating</option><option value={4}>4★ &amp; above</option><option value={5}>5★ only</option>
          </select>
          <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="pms-research-sort">
            <option value="rating">Best research rating</option><option value="return">Highest return</option><option value="aum">Highest AUM</option><option value="name">Name</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PMS_PERIODS.map((value) => (
            <button key={value} onClick={() => setPeriod(value)} className={`rounded-full px-3 py-1 text-xs font-medium ${period === value ? "bg-[var(--brand)] text-white" : "bg-[var(--surface-2)] text-[var(--ink-muted)]"}`} data-testid={`pms-research-period-${value}`}>{value.toUpperCase()}</button>
          ))}
        </div>
      </CardContent></Card>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="pms-research-error">{error}</div>}
      <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-xs" data-testid="pms-research-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Investment approach / Provider</th><th className="p-3 font-medium">Rating</th><th className="p-3 font-medium">Service / Asset class</th><th className="p-3 font-medium">Benchmark</th><th className="p-3 text-right font-medium">AUM</th><th className="p-3 text-right font-medium">{period.toUpperCase()} IA</th><th className="p-3 text-right font-medium">{period.toUpperCase()} benchmark</th><th className="p-3 font-medium">Inception</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {data.strategies.map((row) => (
              <tr key={row.id}>
                <td className="max-w-[360px] p-3"><div className="font-medium text-[var(--brand)]">{row.name}</div><div className="truncate text-[var(--ink-muted)]">{row.provider_name}</div></td>
                <td className="p-3"><Stars n={row.research?.stars} /><div className="mt-1 text-[10px] text-[var(--ink-muted)]">{row.research?.category_rank ? `${row.research.category_rank}/${row.research.category_count}` : "Not rated"}</div></td>
                <td className="p-3"><div>{(row.type || "—").replaceAll("_", " ")}</div><div className="text-[var(--ink-muted)]">{(row.category || "OTHER").replaceAll("_", " ")}</div></td>
                <td className="max-w-[220px] p-3 text-[var(--ink-muted)]">{row.benchmark || "—"}</td>
                <td className="p-3 text-right">{fmtCr(row.aum_crore)}</td>
                <td className={`p-3 text-right font-medium ${(row.returns?.[period] ?? 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtR(row.returns?.[period])}</td>
                <td className="p-3 text-right">{fmtR(row.benchmark_returns?.[period])}</td>
                <td className="p-3">{row.inception_date || "—"}</td>
              </tr>
            ))}
            {!loading && !error && data.strategies.length === 0 && <tr><td colSpan={8} className="p-6 text-sm text-[var(--ink-muted)]">No authorized APMI PMS research data matches these filters. An administrator must import a licensed APMI export before data can be published.</td></tr>}
            {loading && <tr><td colSpan={8} className="p-6 text-sm text-[var(--ink-muted)]">Loading authorized PMS research data…</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
      <p className="text-[10px] leading-4 text-[var(--ink-muted)]">
        PMS returns and benchmark returns are provider-reported figures supplied through the authorized APMI master; they are not SMIFS calculations. SMIFS PMS ratings compare weighted returns, benchmark alpha and benchmark-beating consistency only within each APMI asset class. Approaches without sufficient history remain unrated. Past performance does not indicate future results.
      </p>
    </div>
  );
}

export default function Research() {
  const [ov, setOv] = useState(null);
  const isClient = !!localStorage.getItem("smifs_client_token") || !!localStorage.getItem("client_token");
  const load = useCallback(() => axios.get(`${API}/research/overview`).then((r) => setOv(r.data)), []);
  useEffect(() => {
    load();
    const t = setInterval(() => {
      axios.get(`${API}/research/status`).then((r) => {
        if (r.data.state === "RUNNING") load();
      });
    }, 20000);
    return () => clearInterval(t);
  }, [load]);

  const computing = ov?.status?.state === "RUNNING";
  return (
    <div className="min-h-screen bg-[var(--surface)] pb-16">
      <header className="sticky top-0 z-20 border-b border-[var(--glass-border)] bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-3 py-3 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Microscope className="h-6 w-6 text-[var(--cta)]" />
            <div className="min-w-0">
              <h1 className="truncate font-heading text-base text-[var(--brand)] sm:text-lg">SMIFS Investment Research</h1>
              <div className="hidden text-[10px] text-[var(--ink-muted)] sm:block">Mutual funds and authorized PMS master research · {ov?.rated_funds || 0} mutual funds rated{ov?.computed_at ? ` · updated ${ov.computed_at.slice(0, 10)}` : ""}</div>
            </div>
          </div>
          <Link to={isClient ? "/" : "/login"}>
            <Button size="sm" className="shrink-0 rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="research-login-button">
              <LogIn className="sm:mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">{isClient ? "My Dashboard" : "Client Login"}</span><span className="sm:hidden">{isClient ? "Home" : "Login"}</span>
            </Button>
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-6 px-3 pt-4 sm:px-4 sm:pt-6">
        <Tabs defaultValue="mutual-funds">
          <TabsList>
            <TabsTrigger value="mutual-funds" data-testid="research-product-mf">Mutual Funds</TabsTrigger>
            <TabsTrigger value="pms" data-testid="research-product-pms">PMS</TabsTrigger>
          </TabsList>
          <TabsContent value="mutual-funds" className="space-y-6">
            {ov?.data_state === "PUBLISHED" ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="research-publication-state">
                Validated snapshot {ov.publication?.snapshot_id} · {ov.publication?.methodology} · published {ov.publication?.published_at ? new Date(ov.publication.published_at).toLocaleString() : "—"} · Regular plans only.
              </div>
            ) : ov ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="research-publication-state">
                This research is legacy data whose source entitlement and immutable publication checks have not completed. It is shown with that limitation and is not represented as a validated SMIFS publication.
              </div>
            ) : null}
            {isClient && <Personalized />}
            {computing && <ComputingBanner status={ov.status} onRefresh={load} />}
            {ov?.movers && (
              <Tabs defaultValue="movers">
                <TabsList>
                  <TabsTrigger value="movers" data-testid="research-tab-movers">Top Funds</TabsTrigger>
                  <TabsTrigger value="screener" data-testid="research-tab-screener">Screener</TabsTrigger>
                  <TabsTrigger value="models" data-testid="research-tab-models">Model Portfolios</TabsTrigger>
                  <TabsTrigger value="categories" data-testid="research-tab-categories">Categories</TabsTrigger>
                </TabsList>
                <TabsContent value="movers"><Movers movers={ov.movers} /></TabsContent>
                <TabsContent value="screener"><Screener /></TabsContent>
                <TabsContent value="models"><ModelPortfolios /></TabsContent>
                <TabsContent value="categories"><Categories rows={ov.category_summary} /></TabsContent>
              </Tabs>
            )}
          </TabsContent>
          <TabsContent value="pms"><PmsResearch /></TabsContent>
        </Tabs>
        <p className="pt-4 text-center text-[10px] text-[var(--ink-muted)]">
          SMIFS Star Ratings use source-linked Regular-plan NAV history, a dated risk-free input, returns, Sharpe, drawdown and consistency within SEBI categories. Source state and publication status are displayed above. Not investment advice. Mutual fund investments are subject to market risks. SMIFS Limited — AMFI-registered Mutual Fund Distributor.
        </p>
      </main>
    </div>
  );
}
